import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseApiService } from '@core/api/base-api.service';
import { CreateGroupPayload, GroupDto, UpdateGroupPayload } from '../models/rbac.model';

function tenantQuery(tenantUuid?: string): string {
  return tenantUuid ? `?tenantUuid=${encodeURIComponent(tenantUuid)}` : '';
}

@Injectable({ providedIn: 'root' })
export class GroupService {
  private readonly api = inject(BaseApiService);
  private readonly base = 'v1/admin/rbac';

  private mapGroup(raw: any): GroupDto {
    return {
      accountUuid: raw?.accountUuid ?? '',
      accountId: raw?.accountId ?? undefined,
      groupName: raw?.groupName ?? '',
      email: raw?.email ?? undefined,
      tenantId: raw?.tenantId ?? undefined,
      tenantUuid: raw?.tenantUuid ?? undefined,
      groupType: raw?.groupType ?? undefined,
    };
  }

  getGroups(
    opts: {
      tenantUuid?: string;
      q?: string;
      groupTypes?: string[];
    } = {},
  ): Observable<readonly GroupDto[]> {
    // The list endpoint is paged (20 by default): ask for one large page, as sorting, grouping and
    // pagination are done client-side.
    let params = new HttpParams().set('size', '500');
    if (opts.tenantUuid) params = params.append('tenantUuids', opts.tenantUuid);
    if (opts.q) params = params.set('q', opts.q);
    for (const gt of opts.groupTypes ?? []) params = params.append('groupTypes', gt);
    return this.api.get<any>(`${this.base}/groups`, { params }).pipe(
      map((res) => {
        const data = res?.data ?? res;
        const arr = Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
        return (arr as any[]).map((g) => this.mapGroup(g));
      }),
    );
  }

  createGroup(payload: CreateGroupPayload): Observable<GroupDto> {
    return this.api.post<any>(`${this.base}/groups`, payload).pipe(map((g) => this.mapGroup(g)));
  }

  // Group names (ADMIN...) repeat across tenants: without tenantUuid the API refuses ambiguous names.
  updateGroup(
    groupName: string,
    payload: UpdateGroupPayload,
    tenantUuid?: string,
  ): Observable<GroupDto> {
    return this.api
      .put<any>(
        `${this.base}/groups/${encodeURIComponent(groupName)}${tenantQuery(tenantUuid)}`,
        payload,
      )
      .pipe(map((g) => this.mapGroup(g)));
  }

  deleteGroup(groupName: string, tenantUuid?: string): Observable<void> {
    return this.api
      .delete(`${this.base}/groups/${encodeURIComponent(groupName)}${tenantQuery(tenantUuid)}`)
      .pipe(map(() => void 0));
  }

  assignGroupMember(
    groupName: string,
    accountEmail: string,
    tenantUuid?: string,
  ): Observable<void> {
    return this.api
      .post<void>(`${this.base}/groups/${encodeURIComponent(groupName)}/assign`, {
        accountEmail,
        tenantUuid,
      })
      .pipe(map(() => void 0));
  }

  removeGroupMember(
    groupName: string,
    accountEmail: string,
    tenantUuid?: string,
  ): Observable<void> {
    return this.api
      .delete(
        `${this.base}/groups/${encodeURIComponent(groupName)}/assign/${encodeURIComponent(accountEmail)}` +
          tenantQuery(tenantUuid),
      )
      .pipe(map(() => void 0));
  }
}
